import { defineMessages, MessageDescriptor } from 'react-intl'

import { TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC, WALMART_CARE_OPTION_MODAL_DESCRIPTION_KEY } from './types'
import { NEURODIVERSITY_ESSENTIALS_TITLES } from '../neurodiversityCOE/constants'

// Neurodiversity COE
export const neurodiversityCareOptions = defineMessages({
  neuropsychologicalEvaluation: {
    defaultMessage: 'Neuropsychological evaluation',
    description: 'Care option for neuropsychological evaluation',
  },
  childNeuropsychologicalEvaluation: {
    defaultMessage: 'Child neuropsychological evaluation',
    description: 'Care option for child neuropsychological evaluation',
  },
  treatmentWithAutismInformedProviders: {
    defaultMessage: 'Care with autism-informed providers',
    description: 'Care option for treatment with autism-informed providers',
  },
  supportForASD: {
    defaultMessage: 'Support for autism spectrum disorder',
    description: 'Care option for autism spectrum disorder support',
  },
  childSupportForASD: {
    defaultMessage: 'Child support for autism spectrum disorder',
    description: 'Care option for child autism spectrum disorder support',
  },
  treatmentForADHD: {
    defaultMessage: 'Support for ADHD',
    description: 'Care option for ADHD treatment',
  },
  childTreatmentForADHD: {
    defaultMessage: 'Child support for ADHD',
    description: 'Care option for child ADHD treatment',
  },
  caregiverTraining: {
    defaultMessage: 'Training for caregivers of neurodiverse children',
    description: 'Care option for caregiver training',
  },
  treatmentForOtherNeuroTopics: {
    defaultMessage: 'Support for other neurodiversity topics',
    description: 'Care option for other neurodiversity topics',
  },
  childTreatmentForOtherNeuroTopics: {
    defaultMessage: 'Child support for other neurodiversity topics',
    description: 'Care option for child other neurodiversity topics',
  },
})

export const neurodiversityCareOptionsDescriptions = defineMessages({
  // Self descriptions for adult or teen
  neuropsychologicalEvaluationSelf: {
    defaultMessage:
      'Comprehensive testing to explore how your brain works, diagnose possible conditions, and gain insight.',
    description: 'Description for self neuropsychological evaluation',
  },
  treatmentWithAutismInformedProviders: {
    defaultMessage: 'Work with a provider to navigate social expectations, sensory sensitivities, burnout, and more.',
    description: 'Description for treatment with autism-informed providers',
  },
  treatmentWithAutismInformedProvidersForTeen: {
    defaultMessage:
      'Work with a provider to navigate social expectations, sensory sensitivities, prevent overwhelm, and more.',
    description: 'Description for treatment with autism-informed providers for teen',
  },
  treatmentForADHDSelf: {
    defaultMessage: 'Navigate difficulties like organization and focus while supporting your energy and creativity.',
    description: 'Description for self ADHD treatment',
  },
  caregiverTraining: {
    defaultMessage: 'Learn strategies to support your child’s needs, behaviors, communication, and more.',
    description: 'Description for caregiver training (same for both contexts)',
  },
  treatmentForOtherNeuroTopicsSelf: {
    defaultMessage: "Meet with a neurodiversity-aware provider to navigate life's demands while honoring who you are.",
    description: 'Description for self other neurodiversity topics',
  },

  // Child descriptions
  neuropsychologicalEvaluationChild: {
    defaultMessage: 'Comprehensive testing to gain insights and diagnose conditions and challenges for your child.',
    description: 'Description for child neuropsychological evaluation',
  },
  supportForASD: {
    defaultMessage: 'Help your child navigate social expectations, sensory sensitivities, and prevent overwhelm.',
    description: 'Description for child autism spectrum disorder support',
  },
  treatmentForADHDChild: {
    defaultMessage: 'Help your child focus, regulate their emotions, and more so they can thrive as they are.',
    description: 'Description for child ADHD treatment',
  },
  treatmentForOtherNeuroTopicsChild: {
    defaultMessage: 'Help your neurodivergent child navigate challenges and focus on their unique needs.',
    description: 'Description for child other neurodiversity topics',
  },
})

export const neurodiversityCareOptionsModalTitle = defineMessages({
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_NEUROPSYCHOLOGICAL_EVALUATION]: {
    defaultMessage: 'Neuropsychological evaluation',
    description: 'Self care option modal title for adult and teen neuropsychological evaluation',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_TREATMENT_FOR_AUTISM]: {
    defaultMessage: 'Care with autism-informed providers',
    description: 'Self care option modal title for adult and teen treatment with autism-informed providers',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_TREATMENT_FOR_ADHD]: {
    defaultMessage: 'Support for ADHD',
    description: 'Self care option modal title for adult and teen treatment with autism-informed providers',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_OTHER_NEURODIVERSITY_TOPICS]: {
    defaultMessage: 'Support for other neurodiversity topics',
    description: 'Self care option modal title for teen treatment for other neurodiversity topics',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_NEUROPSYCHOLOGICAL_EVALUATION_CHILD]: {
    defaultMessage: 'Child neuropsychological evaluation',
    description: 'Self care option modal title for child neuropsychological evaluation',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_TREATMENT_FOR_AUTISM_CHILD]: {
    defaultMessage: 'Child support for autism spectrum disorder',
    description: 'Self care option card for child support for autism spectrum disorder',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_TREATMENT_FOR_ADHD_CHILD]: {
    defaultMessage: 'Child support for ADHD',
    description: 'Self care option modal title for child treatment for ADHD',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_OTHER_NEURODIVERSITY_TOPICS_CHILD]: {
    defaultMessage: 'Child support for other neurodiversity topics',
    description: 'Self care option modal title for child treatment for other neurodiversity topics',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_CAREGIVER_TRAINING_FOR_NEURODIVERSE_CHILD]: {
    defaultMessage: 'Training for caregivers of neurodiverse children',
    description: 'Self care option modal title for child caregiver training for neurodiverse children',
  },
})

export const neurodiversityCareOptionsModalDescriptions = defineMessages({
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_NEUROPSYCHOLOGICAL_EVALUATION]: {
    defaultMessage:
      'Work with a provider to seek comprehensive testing and gain insights on how your brain works, objectively measure key cognitive abilities, and accurately diagnose conditions affecting brain function.',
    description: 'Self care option modal description for adult and teen neuropsychological evaluation',
  },
  [WALMART_CARE_OPTION_MODAL_DESCRIPTION_KEY.WALMART_NEUROPSYCHOLOGICAL_EVALUATION]: {
    defaultMessage:
      'Medical plan members and their dependents enrolled in a Walmart-sponsored Premier PPO, Contribution HRA, Saver HSA, or Personalized Wellbeing Copay Plan provided by Skai BCBS or UMR, and meet clinical criteria, are eligible for this program. Please contact your health plan to understand your coverage.',
    description: 'Walmart self care option modal description for adult and teen neuropsychological evaluation',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_TREATMENT_FOR_AUTISM]: {
    defaultMessage:
      'Autistic people experience the world in diverse ways, and it can be a challenge to navigate social expectations, sensory sensitivities, or burnout. Our providers can help with strategies to reduce stress and tools to help you thrive without masking who you are.',
    description: 'Self care option modal description for teen treatment with autism-informed providers',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_TREATMENT_FOR_ADHD]: {
    defaultMessage:
      'If you’re living with ADHD and looking for support, our mental health providers can meet you where you are to navigate difficulties like focus, organization, and emotional regulation, while supporting your energy and creativity.',
    description: 'Self care option modal description for teen treatment for ADHD',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_OTHER_NEURODIVERSITY_TOPICS]: {
    defaultMessage:
      'Neurodivergence includes many ways of thinking, sensing, and processing the world. Our mental health support is inclusive, strengths-based, and flexible, helping you navigate life’s demands while honoring who you are.',
    description: 'Self care option modal description for teen treatment for other neurodiversity topics',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_NEUROPSYCHOLOGICAL_EVALUATION_CHILD]: {
    defaultMessage:
      'Work with a provider to seek comprehensive testing and gain insights on how your child’s brain works, objectively measure key cognitive abilities, and accurately diagnose conditions and challenges that may impact their daily life.',
    description: 'Self care option modal description for child neuropsychological evaluation',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_TREATMENT_FOR_AUTISM_CHILD]: {
    defaultMessage:
      'If your child is autistic, you can find support with a neurodiversity-affirming provider who can offer care tailored to your child’s lived experience, helping them navigate social expectations, sensory sensitivity, prevent burnout, and more.',
    description: 'Self care option modal description for child support for autism spectrum disorder',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_TREATMENT_FOR_ADHD_CHILD]: {
    defaultMessage:
      'If your child has ADHD, a neurodiversity-affirming provider can offer support tailored to your child’s needs, helping them with focus, organization, emotional regulation, and daily life.',
    description: 'Self care option modal description for child treatment for ADHD',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_OTHER_NEURODIVERSITY_TOPICS_CHILD]: {
    defaultMessage:
      'In seeking support for your neurodivergent child, providers can offer care tailored to their unique needs, focusing on their strengths and helping them navigate challenges in their daily life.',
    description: 'Self care option modal description for child treatment for other neurodiversity topics',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.NEURODIVERSITY_CAREGIVER_TRAINING_FOR_NEURODIVERSE_CHILD]: {
    defaultMessage:
      'Training for caregivers of neurodiverse children can equip you with the skills to better support your child’s unique needs and to foster an environment where your child and your family can thrive.',
    description: 'Self care option modal description for child caregiver training for neurodiverse children',
  },
})

// Health Challenges COE
export const healthChallengesCareOptionTitles = defineMessages({
  bodyImage: {
    defaultMessage: 'Body image concerns',
    description: 'Health challenges care option title',
  },
  sleepIssues: {
    defaultMessage: 'Sleep issues',
    description: 'Health challenges care option title',
  },
  cancerSurvivorship: {
    defaultMessage: 'Cancer survivorship',
    description: 'Health challenges care option title',
  },
  chronicPain: {
    defaultMessage: 'Chronic pain',
    description: 'Health challenges care option title',
  },
  otherHealthConditions: {
    defaultMessage: 'Other health conditions',
    description: 'Health challenges care option title',
  },
})

export const healthChallengesCareOptionDescriptions = defineMessages({
  bodyImage: {
    defaultMessage: 'Meet 1-on-1 with a provider to understand and consider addressing body image or weight concerns.',
    description: 'Health challenges care option description',
  },
  sleepIssues: {
    defaultMessage:
      "Work with a provider to discover what's getting in the way of your sleep and get back on track for better rest.",
    description: 'Health challenges care option description',
  },
  cancerSurvivorship: {
    defaultMessage:
      'Get comprehensive support for needs related to a cancer diagnosis or the diagnosis of a loved one.',
    description: 'Health challenges care option description',
  },
  chronicPain: {
    defaultMessage: 'Get mental health support for the physical, psychological, and emotional effects of chronic pain.',
    description: 'Health challenges care option description',
  },
  otherHealthConditions: {
    defaultMessage: 'Meet with a provider for support with any chronic health issues you may be going through.',
    description: 'Health challenges care option description',
  },
})

export const healthChallengesCareOptionsModalTitle = defineMessages({
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.HEALTH_CHALLENGES_BODY_IMAGE_ISSUES]: {
    defaultMessage: 'Get mental health support for body image concerns',
    description: 'Health challenges care option modal title for body image concerns',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.HEALTH_CHALLENGES_SLEEP_ISSUES]: {
    defaultMessage: 'Get mental health support for sleep issues',
    description: 'Health challenges care option modal title for sleep issues',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.HEALTH_CHALLENGES_CANCER_SURVIVORSHIP]: {
    defaultMessage: 'Get mental health support for cancer survivorship',
    description: 'Health challenges care option modal title for cancer survivorship',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.HEALTH_CHALLENGES_CHRONIC_PAIN]: {
    defaultMessage: 'Get mental health support for chronic pain',
    description: 'Health challenges care option modal title for chronic pain',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.HEALTH_CHALLENGES_OTHER_CHRONIC_HEALTH_CONDITIONS]: {
    defaultMessage: 'Get mental health support for other health conditions',
    description: 'Health challenges care option modal title for other health conditions',
  },
})

export const healthChallengesCareOptionsModalDescriptions = defineMessages({
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.HEALTH_CHALLENGES_BODY_IMAGE_ISSUES]: {
    defaultMessage:
      "If you're facing body image or weight concerns, meet 1-on-1 with a provider to understand what you're going through and develop a personalized plan.",
    description: 'Health challenges care option modal description for body image concerns',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.HEALTH_CHALLENGES_SLEEP_ISSUES]: {
    defaultMessage:
      "Our mental health providers are here for you to help discover what's getting in the way of your sleep and to develop a personalized plan to get you on track for better rest.",
    description: 'Health challenges care option modal description for sleep issues',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.HEALTH_CHALLENGES_CANCER_SURVIVORSHIP]: {
    defaultMessage:
      'People diagnosed with cancer and their loved ones often deal with significant distress and deserve comprehensive support. Our mental health providers can help you through any associated grief, insomnia, body image concerns, and more.',
    description: 'Health challenges care option modal description for cancer survivorship',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.HEALTH_CHALLENGES_CHRONIC_PAIN]: {
    defaultMessage:
      'Meet 1-on-1 with a mental health provider who can support you through the physical, psychological, and emotional components of chronic pain.',
    description: 'Health challenges care option modal description for chronic pain',
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.HEALTH_CHALLENGES_OTHER_CHRONIC_HEALTH_CONDITIONS]: {
    defaultMessage:
      'Our mental health providers are here to support you, learn about any chronic health issues you may be going through, and help you develop a personalized wellness plan.',
    description: 'Health challenges care option modal description for other health conditions',
  },
})

export const healthChallengesToolkitMessages = defineMessages({
  headerTitle: {
    defaultMessage: "Let's help with health challenges",
    description: 'Health challenges toolkit header text',
  },
  headerSubtitle: {
    defaultMessage:
      'Living with chronic health challenges can take a toll on your mental health. Lyra can connect you with care options to help support your overall well-being.',
    description: 'Health challenges toolkit header subtitle text',
  },
  needsListTitle: {
    defaultMessage: 'Care options to match your unique needs',
    description: 'Header for the health challenges needs list section',
  },
  exploreCareOptionsButton: {
    defaultMessage: 'Explore care options',
    description: 'Button to explore health challenges care options',
  },
  footerTitle: {
    defaultMessage: 'Looking for something else?',
    description: 'Health challenges toolkit footer title',
  },
  footerDescription: {
    defaultMessage:
      "We're here to help you find care that matches your needs. To get started, we'll ask a few questions to find out which options would work best for you.",
    description: 'Health challenges toolkit footer description',
  },
  footerButton: {
    defaultMessage: 'Get started',
    description: 'Health challenges toolkit footer button',
  },
  essentialsSectionTitle: {
    defaultMessage: 'Resources for healthy living',
    description: 'Title for the health challenges essentials section',
  },
})

export const healthChallengesEssentialsContent: { title: MessageDescriptor; articleIds: number[] }[] = [
  {
    title: healthChallengesToolkitMessages.essentialsSectionTitle,
    articleIds: [33836, 2126329, 36230, 2496381, 2516814],
  },
]

export const womensHealthEssentialsContent: { title: MessageDescriptor; articleIds: number[] }[] = [
  {
    title: NEURODIVERSITY_ESSENTIALS_TITLES.ROLES_AND_RELATIONSHIPS,
    articleIds: [2343315, 1974804, 1974452, 31889, 30456],
  },
]

// Women's Health COE
export const womensHealthCareOptionTitles = defineMessages({
  pregnancy: {
    defaultMessage: 'Pregnancy',
    description: "Women's health care option title for pregnancy",
  },
  fertilityIssues: {
    defaultMessage: 'Fertility challenges',
    description: "Women's health care option title for fertility challenges",
  },
  menopause: {
    defaultMessage: 'Menopause',
    description: "Women's health care option title for menopause",
  },
  premenstrualSyndrome: {
    defaultMessage: 'Premenstrual distress',
    description: "Women's health care option title for premenstrual distress",
  },
  mentalLoad: {
    defaultMessage: 'Mental load',
    description: "Women's health care option title for mental load",
  },
  selfEsteem: {
    defaultMessage: 'Self-esteem',
    description: "Women's health care option title for self-esteem",
  },
  caregiving: {
    defaultMessage: 'Caregiving',
    description: "Women's health care option title for caregiving",
  },
  otherWomensHealthIssues: {
    defaultMessage: "Other women's mental health topics",
    description: "Women's health care option title for other topics",
  },
})

export const womensHealthCareOptionDescriptions = defineMessages({
  pregnancy: {
    defaultMessage: 'Navigate the changes that pregnancy and the postpartum period often bring.',
    description: "Women's health care option description for pregnancy",
  },
  fertilityIssues: {
    defaultMessage: 'Get help with fertility issues and learn strategies to manage challenges.',
    description: "Women's health care option description for fertility challenges",
  },
  menopause: {
    defaultMessage: 'Develop a balanced approach to this new chapter in your life.',
    description: "Women's health care option description for menopause",
  },
  premenstrualSyndrome: {
    defaultMessage: 'Learn how to better cope with hormonal changes, premenstrual distress, and more.',
    description: "Women's health care option description for premenstrual distress",
  },
  mentalLoad: {
    defaultMessage: 'Learn to navigate invisible, behind-the-scenes work, such as planning, organizing, and more.',
    description: "Women's health care option description for mental load",
  },
  selfEsteem: {
    defaultMessage: 'Work through self-esteem challenges and create a more balanced view of yourself.',
    description: "Women's health care option description for self-esteem",
  },
  caregiving: {
    defaultMessage: 'Work through challenges that come from caring for others.',
    description: "Women's health care option description for caregiving",
  },
  otherWomensHealthIssues: {
    defaultMessage: 'Develop a personalized plan by putting your needs as a woman at the forefront.',
    description: "Women's health care option description for other topics",
  },
})

export const womensHealthCareOptionsModalTitle = defineMessages({
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.WOMENS_MENTAL_HEALTH_PREGNANCY]: {
    defaultMessage: 'Get mental health support for <bold>pregnancy</bold>',
    description: "Women's health care option modal title for pregnancy",
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.WOMENS_MENTAL_HEALTH_FERTILITY_ISSUES]: {
    defaultMessage: 'Get mental health support for <bold>fertility challenges</bold>',
    description: "Women's health care option modal title for fertility challenges",
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.WOMENS_MENTAL_HEALTH_MENOPAUSE]: {
    defaultMessage: 'Get mental health support for <bold>menopause</bold>',
    description: "Women's health care option modal title for menopause",
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.WOMENS_MENTAL_HEALTH_PREMENSTRUAL_SYNDROME]: {
    defaultMessage: 'Get mental health support for <bold>premenstrual distress</bold>',
    description: "Women's health care option modal title for premenstrual distress",
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.WOMENS_MENTAL_HEALTH_MENTAL_LOAD]: {
    defaultMessage: 'Get mental health support for <bold>mental load</bold>',
    description: "Women's health care option modal title for mental load",
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.WOMENS_MENTAL_HEALTH_SELF_ESTEEM]: {
    defaultMessage: 'Get mental health support for <bold>self-esteem</bold>',
    description: "Women's health care option modal title for self-esteem",
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.WOMENS_MENTAL_HEALTH_CAREGIVING]: {
    defaultMessage: 'Get mental health support for <bold>caregiving</bold>',
    description: "Women's health care option modal title for caregiving",
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.WOMENS_MENTAL_HEALTH_OTHER_WOMENS_MENTAL_HEALTH_ISSUES]: {
    defaultMessage: "Get mental health support for <bold>other women's mental health issues</bold>",
    description: "Women's health care option modal title for other topics",
  },
})

export const womensHealthCareOptionsModalDescriptions = defineMessages({
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.WOMENS_MENTAL_HEALTH_PREGNANCY]: {
    defaultMessage:
      'Our mental health providers are here to support you 1-on-1 to navigate the changes that pregnancy and the postpartum period often bring.',
    description: "Women's health care option modal description for pregnancy",
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.WOMENS_MENTAL_HEALTH_FERTILITY_ISSUES]: {
    defaultMessage:
      'Meet with a mental health provider who understands fertility issues and can help you learn skills and strategies to manage challenges.',
    description: "Women's health care option modal description for fertility challenges",
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.WOMENS_MENTAL_HEALTH_MENOPAUSE]: {
    defaultMessage:
      '1-on-1 support with one of our mental health providers can help you develop a balanced approach to this new chapter in your life.',
    description: "Women's health care option modal description for menopause",
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.WOMENS_MENTAL_HEALTH_PREMENSTRUAL_SYNDROME]: {
    defaultMessage:
      'Meet 1-on-1 with a provider to learn how to better cope with hormonal changes, premenstrual distress, and more.',
    description: "Women's health care option modal description for premenstrual distress",
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.WOMENS_MENTAL_HEALTH_MENTAL_LOAD]: {
    defaultMessage:
      'Meet with a mental health provider to help better manage your mental load—focusing on your invisible and behind-the-scenes work, such as planning, organizing, and more.',
    description: "Women's health care option modal description for mental load",
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.WOMENS_MENTAL_HEALTH_SELF_ESTEEM]: {
    defaultMessage:
      'Meet with a mental health provider who can help you work through self-esteem challenges and create a more balanced view of yourself.',
    description: "Women's health care option modal description for self-esteem",
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.WOMENS_MENTAL_HEALTH_CAREGIVING]: {
    defaultMessage:
      'Meet with a provider who understands how stressful it can be providing emotional, financial, physical, and social care to others.',
    description: "Women's health care option modal description for caregiving",
  },
  [TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC.WOMENS_MENTAL_HEALTH_OTHER_WOMENS_MENTAL_HEALTH_ISSUES]: {
    defaultMessage:
      'Our mental health providers are here to support you and help you develop a personalized plan by putting your needs as a woman at the forefront.',
    description: "Women's health care option modal description for other topics",
  },
})
